import { useQuery } from '@apollo/client/react';
import { StarBorder } from '@mui/icons-material';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  ButtonBase,
} from '@mui/material';
import {
  // Ads,
  CompetitionRanking,
  CompetitionRankingRow,
} from '@scorenco/components';
import {
  ssrApolloService,
  RouteLink,
  useRouteInfo,
  Types,
} from '@scorenco/core';
import {
  NextAdArea,
  NextAkceloConfig,
  NextGooglePublisherTagConfig,
  NextOptiDigitalAdArea,
  NextOptiDigitalConfig,
  NextPageWithLayout,
} from '@scorenco/next';

import { PageLayout, SportChip } from '@/components';
import { WebLayout } from '@/layouts';
import { RouteKey } from '@/routes';
import { getSport } from '@/utils';

import { CompetitionQueryDocument } from './_internal/Competition.query.generated';
import { CompetitionMetadata } from './_internal/components/CompetitionMetadata';
import { CompetitionRound } from './_internal/components/CompetitionRound';
import { competitionQueryParams } from './_internal/utils/competitionQueryParams';

const Competition: NextPageWithLayout<{ sport: Types.Clubs_Sport }> = ({
  sport,
}) => {
  const routeInfo = useRouteInfo();
  const urlInfo = competitionQueryParams(routeInfo.query);

  const competitionQuery = useQuery(CompetitionQueryDocument, {
    variables: { id: urlInfo.competitionId as number },
    skip: !urlInfo.competitionId,
    context: {
      role: 'anonymous',
    },
  });
  const competition =
    competitionQuery.data?.competitions_competition_detail_by_id?.[0];
  const competitionRankings = competition?.rankings;

  return (
    <PageLayout
      image={competition?.sport?.image}
      breadcrumbs={[
        {
          label: sport?.name || urlInfo.sport,
          href: {
            routeKey: RouteKey.Competitions,
            params: {
              sport: urlInfo.sport,
            },
          },
        },
        {
          label: competition?.league?.name || '',
          href: {
            routeKey: RouteKey.League,
            params: {
              sport: urlInfo.sport,
              league: `${
                competition?.league?.slug
              }-${competition?.league?.id.toString(36)}`,
            },
          },
        },
        {
          label: competition?.level?.name || '',
          href: {
            routeKey: RouteKey.Level,
            params: {
              sport: urlInfo.sport,
              level: `${competition?.level_slug}-${competition?.level_id?.toString(
                36
              )}`,
            },
          },
        },
        {
          label: competition?.name || '',
        },
      ]}
    >
      <CompetitionMetadata competition={competition} />
      <NextOptiDigitalConfig adUnit="/157593316/od.scorenco.com/competition" />
      <NextAkceloConfig />
      <NextGooglePublisherTagConfig />

      <Paper
        variant="bordered"
        sx={{
          p: { xs: 2, sm: 3, lg: 4 },
          borderRadius: '0 0 0.5rem 0.5rem',
        }}
      >
        <Stack width="100%" alignItems="center" gap={2}>
          <SportChip sport={urlInfo.sport} />

          <Typography variant="h2" component="h1">
            {competition?.name}
          </Typography>

          <Button
            component={RouteLink}
            href={
              {
                routeKey: RouteKey.Connect,
                params: { redirectTo: routeInfo.asPath, state: 'signup' },
              } as unknown as string
            }
            variant="outlined"
            startIcon={<StarBorder />}
          >
            Suivre cette compétition
          </Button>
        </Stack>
      </Paper>

      {/* TODO: Comment this when we have a campaign */}
      <NextAdArea
        akceloSlotId="Scorenco_PlayRVideo"
        akceloAdUnitClass="akcelo-adunit-15786"
        withGooglePublisherTag
      />
      <NextOptiDigitalAdArea areaId="Billboard_1" />
      <NextOptiDigitalAdArea areaId="Mobile_Pos1" />
      {/* {sport?.id && (
        <Ads
          type="competition"
          sport_id={sport?.id}
          style={{ marginTop: '1rem' }}
          club_name={competition?.name}
        />
      )} */}

      <Stack direction={{ xs: 'column', lg: 'row' }} gap={4} my={4}>
        {competitionRankings && (
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" sx={{ mb: 2 }}>
              Classement
            </Typography>
            <Paper
              variant="bordered"
              sx={{ p: 0, alignItems: 'stretch', overflow: 'hidden' }}
            >
              <CompetitionRanking
                sport={competition?.sport}
                rankings={competitionRankings}
                slots={{
                  row: ({ ranking, ...rest }) => (
                    <ButtonBase
                      component={RouteLink}
                      href={ranking?.url}
                      sx={{ display: 'block' }}
                    >
                      <CompetitionRankingRow ranking={ranking} {...rest} />
                    </ButtonBase>
                  ),
                }}
              />
            </Paper>
          </Box>
        )}

        <CompetitionRound
          competition={competition}
          roundId={
            competition?.competition?.current_round?.[0]?.id || urlInfo.roundId
          }
        />
      </Stack>

      <NextOptiDigitalAdArea areaId="Billboard_Bottom" />
      <NextOptiDigitalAdArea areaId="Mobile_Bottom" />
    </PageLayout>
  );
};

export const getServerSideProps = WebLayout.buildGetServerSideProps({}, [
  async ({ query }, previous) => {
    const { apolloClient, addApolloState } = ssrApolloService();

    if (query['no-ssr'] === undefined) {
      const { competitionId, sport: sportSlug } = competitionQueryParams(query);

      if (competitionId && sportSlug) {
        const competitionQuery = await apolloClient.query({
          query: CompetitionQueryDocument,
          variables: { id: competitionId },
        });
        const competition =
          competitionQuery.data?.competitions_competition_detail_by_id?.[0];

        if (competition) {
          const sport = await getSport(sportSlug);

          return addApolloState({
            ...previous,
            props: {
              ...previous.props,
              sport,
            },
          });
        }
      }

      return { notFound: true };
    }

    return addApolloState(previous);
  },
]);

Competition.getLayout = WebLayout.buildGetLayout({
  strapiMenu: true,
  amplify: { protectedPage: false },
});

export default Competition;
